"use client"

import type { GooglePlacesType } from "@/modules/google/types"
import type { FunctionComponent, PropsWithChildren } from "react"

import Script from "next/script"
import { useEffect, useMemo, useState } from "react"

import { GooglePlacesContext } from "@/modules/google/useGooglePlaces"

type GooglePlacesProviderProps = PropsWithChildren<{
  googleApiNotReadyErrorText: string
  googlePlacesApiKey: string
  missingApiKeyErrorText: string
}>

const isGoogleScriptReadyToBeUsed = (): boolean => {
  if (typeof window === "undefined") {
    return false
  }

  if (!window.google?.maps?.places) {
    return false
  }

  return (
    window.google.maps.places?.AutocompleteService &&
    window.google.maps.places?.PlacesService &&
    window.google.maps.places?.AutocompleteSessionToken
  )
}

export const GooglePlacesProvider: FunctionComponent<GooglePlacesProviderProps> = ({
  children,
  googleApiNotReadyErrorText,
  googlePlacesApiKey,
  missingApiKeyErrorText,
}) => {
  const [isGoogleScriptReady, setIsGoogleScriptReady] = useState(false)

  const onGoogleApiReady = (): void => {
    setIsGoogleScriptReady(isGoogleScriptReadyToBeUsed)
  }

  // useful to be able to enjoy the "ready" callback from googleapis script (passed as param)
  if (typeof window !== "undefined") {
    window.onGoogleApiReady = onGoogleApiReady
  }

  const googlePlaces = useMemo((): GooglePlacesType => {
    const defaultResponse: GooglePlacesType = {
      autocompleteService: null,
      errorText: "",
      googleServiceSessionToken: null,
      isGoogleScriptReady: false,
      placesService: null,
    }

    if (!googlePlacesApiKey) {
      return {
        ...defaultResponse,
        errorText: missingApiKeyErrorText,
      }
    }

    if (!isGoogleScriptReady) {
      return {
        ...defaultResponse,
        errorText: googleApiNotReadyErrorText,
      }
    }

    return {
      autocompleteService: new window.google.maps.places.AutocompleteService(),
      errorText: "",
      googleServiceSessionToken: new window.google.maps.places.AutocompleteSessionToken(),
      isGoogleScriptReady,
      placesService: new window.google.maps.places.PlacesService(document.createElement("div")),
    }
  }, [googleApiNotReadyErrorText, missingApiKeyErrorText, googlePlacesApiKey, isGoogleScriptReady])

  // So the Provider could be set up correctly in case the script was already appended once
  useEffect(() => {
    onGoogleApiReady()
  }, [])

  return (
    <GooglePlacesContext.Provider value={googlePlaces}>
      {children}
      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${googlePlacesApiKey}&language=en&libraries=places&callback=onGoogleApiReady`}
      />
    </GooglePlacesContext.Provider>
  )
}
